import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI, uploadAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Upload, Loader2, FileText, X, Image as ImageIcon, BookOpen, GraduationCap, Palette, Sparkles, Frame, Link2, Info } from 'lucide-react';

const PRODUCT_TYPES = [
  { value: 'Ebook', label: 'Ebook', description: 'PDF, EPUB or digital books', icon: BookOpen, color: 'bg-blue-50 border-blue-200 text-blue-700', iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
  { value: 'Course', label: 'Course', description: 'Video courses and tutorials', icon: GraduationCap, color: 'bg-emerald-50 border-emerald-200 text-emerald-700', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
  { value: 'Template', label: 'Template', description: 'Design or document templates', icon: Palette, color: 'bg-amber-50 border-amber-200 text-amber-700', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
  { value: 'Preset', label: 'Preset', description: 'Photo/video presets and filters', icon: Sparkles, color: 'bg-purple-50 border-purple-200 text-purple-700', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
  { value: 'Poster', label: 'Poster', description: 'Digital posters and wall art', icon: Frame, color: 'bg-rose-50 border-rose-200 text-rose-700', iconBg: 'bg-rose-100', iconColor: 'text-rose-600' },
];

const STEPS = [
  { id: 'type', label: 'Type' },
  { id: 'details', label: 'Details' },
  { id: 'file', label: 'Upload' },
  { id: 'settings', label: 'Settings' },
];

export default function ProductCreatePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);

  const [form, setForm] = useState({
    type: '',
    name: '',
    description: '',
    is_free: false,
    price: '',
    currency: 'MYR',
    file_id: '',
    file_name: '',
    thumbnail_url: '',
    access_link: '',
    max_downloads: 5,
    expiry_hours: 72,
    is_published: false,
  });

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadAPI.file(fd);
      updateForm('file_id', res.data.file_id);
      updateForm('file_name', res.data.filename);
      toast.success(`File uploaded: ${res.data.filename}`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadAPI.thumbnail(fd);
      updateForm('thumbnail_url', res.data.thumbnail_url);
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error('Thumbnail upload failed');
    } finally {
      setUploadingThumb(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return !!form.type;
      case 1: return form.name.trim() && (form.is_free || (form.price && parseFloat(form.price) > 0));
      case 2: return true; // File is optional
      case 3: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handlePublish = async (publish = false) => {
    if (!form.name.trim()) { toast.error('Product name is required'); return; }
    if (!form.is_free && (!form.price || parseFloat(form.price) <= 0)) { toast.error('Valid price is required'); return; }
    setSaving(true);
    try {
      await productsAPI.create({
        ...form,
        price: form.is_free ? 0 : parseFloat(form.price),
        is_published: publish,
      });
      toast.success(publish ? 'Product published!' : 'Product saved as draft');
      navigate('/app/products');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const selectedType = PRODUCT_TYPES.find(t => t.value === form.type);

  return (
    <div className="max-w-[720px] mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/products')} className="shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold tracking-tight">Create Product</h1>
          <p className="text-sm text-muted-foreground">Set up your digital product step by step</p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-1 mb-8">
        {STEPS.map((step, i) => (
          <div key={step.id} className="flex items-center flex-1">
            <button
              onClick={() => { if (i <= currentStep) setCurrentStep(i); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full
                ${i === currentStep ? 'bg-primary text-primary-foreground' : i < currentStep ? 'bg-accent text-accent-foreground cursor-pointer' : 'bg-muted text-muted-foreground'}`}
            >
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0
                ${i === currentStep ? 'bg-primary-foreground text-primary' : i < currentStep ? 'bg-accent-foreground/20 text-accent-foreground' : 'bg-muted-foreground/20'}`}>
                {i < currentStep ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className="hidden sm:inline">{step.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className="h-px w-4 bg-border shrink-0" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {/* Step 1: Product Type */}
        {currentStep === 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">What are you selling?</h2>
              <p className="text-sm text-muted-foreground mt-1">Choose the type of digital product</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-type-picker">
              {PRODUCT_TYPES.map(type => {
                const Icon = type.icon;
                const isSelected = form.type === type.value;
                return (
                  <button
                    key={type.value}
                    onClick={() => updateForm('type', type.value)}
                    data-testid={`product-type-${type.value.toLowerCase()}`}
                    className={`relative flex items-start gap-4 p-5 rounded-xl border-2 text-left transition-all
                      ${isSelected ? `${type.color} border-current shadow-sm` : 'border-border bg-card hover:border-primary/30 hover:shadow-sm'}`}
                  >
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${isSelected ? type.iconBg : 'bg-muted'}`}>
                      <Icon className={`h-6 w-6 ${isSelected ? type.iconColor : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold">{type.label}</p>
                      <p className={`text-sm mt-0.5 ${isSelected ? 'opacity-80' : 'text-muted-foreground'}`}>{type.description}</p>
                    </div>
                    {isSelected && (
                      <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-current flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Product Details</h2>
              <p className="text-sm text-muted-foreground mt-1">Tell your customers about this product</p>
            </div>

            {selectedType && (
              <Badge variant="secondary" className={selectedType.color}>
                {selectedType.label}
              </Badge>
            )}

            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Product Name</Label>
                <Input
                  value={form.name}
                  onChange={e => updateForm('name', e.target.value)}
                  placeholder={`e.g. ${form.type === 'Ebook' ? 'Instagram Marketing Guide' : form.type === 'Course' ? 'Complete Photography Masterclass' : form.type === 'Template' ? 'Instagram Templates Pack' : form.type === 'Preset' ? 'Moody Film Presets' : 'Motivational Poster Set'}`}
                  className="h-12 text-base"
                  data-testid="product-form-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={e => updateForm('description', e.target.value)}
                  placeholder="Describe what your customers will get..."
                  rows={4}
                  className="text-base resize-none"
                />
                <p className="text-xs text-muted-foreground">{form.description.length}/500 characters</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Pricing</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { updateForm('is_free', true); updateForm('price', ''); }}
                    className={`flex-1 p-4 rounded-xl border-2 text-center transition-all ${form.is_free ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-border hover:border-primary/30'}`}
                    data-testid="product-pricing-free"
                  >
                    <p className="font-semibold text-base">Free</p>
                    <p className="text-xs mt-0.5 opacity-70">No payment required</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => updateForm('is_free', false)}
                    className={`flex-1 p-4 rounded-xl border-2 text-center transition-all ${!form.is_free ? 'border-[#195259] bg-[#195259]/5 text-[#195259]' : 'border-border hover:border-primary/30'}`}
                    data-testid="product-pricing-paid"
                  >
                    <p className="font-semibold text-base">Paid</p>
                    <p className="text-xs mt-0.5 opacity-70">Set your price</p>
                  </button>
                </div>
              </div>

              {!form.is_free && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Price</Label>
                  <div className="flex gap-3">
                    <select
                      value={form.currency}
                      onChange={e => updateForm('currency', e.target.value)}
                      className="h-12 px-3 rounded-lg border border-border bg-card text-sm font-medium w-24 shrink-0"
                      data-testid="product-currency-select"
                    >
                      <option value="MYR">RM</option>
                      <option value="IDR">Rp</option>
                      <option value="USD">$</option>
                    </select>
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={form.price}
                      onChange={e => updateForm('price', e.target.value)}
                      placeholder="0.00"
                      className="h-12 text-base font-semibold flex-1"
                      data-testid="product-form-price-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: File Upload */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Upload Files</h2>
              <p className="text-sm text-muted-foreground mt-1">Upload your digital product file and cover image</p>
            </div>

            {/* Product File */}
            <Card>
              <CardContent className="pt-6">
                <Label className="text-sm font-medium mb-3 block">Product File</Label>
                {form.file_name ? (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-accent">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <FileText className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{form.file_name}</p>
                      <p className="text-xs text-muted-foreground">File uploaded successfully</p>
                    </div>
                    <Button variant="ghost" size="icon" className="shrink-0" onClick={() => { updateForm('file_id', ''); updateForm('file_name', ''); }}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="product-form-file-upload" />
                    <div className="border-2 border-dashed rounded-xl p-8 text-center hover:border-primary/50 hover:bg-accent/50 transition-colors">
                      {uploading ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-sm font-medium">Uploading...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3">
                          <div className="h-14 w-14 rounded-xl bg-muted flex items-center justify-center">
                            <Upload className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Click to upload your file</p>
                            <p className="text-xs text-muted-foreground mt-1">PDF, ZIP, MP4, or any file up to 100MB</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                )}
              </CardContent>
            </Card>

            {/* Thumbnail */}
            <Card>
              <CardContent className="pt-6">
                <Label className="text-sm font-medium mb-3 block">Cover Image</Label>
                {form.thumbnail_url ? (
                  <div className="relative w-full max-w-xs">
                    <img src={form.thumbnail_url} alt="Thumbnail" className="w-full aspect-video rounded-xl object-cover" />
                    <Button variant="destructive" size="icon" className="absolute top-2 right-2 h-8 w-8" onClick={() => updateForm('thumbnail_url', '')}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={handleThumbUpload} disabled={uploadingThumb} />
                    <div className="border-2 border-dashed rounded-xl p-8 text-center hover:border-primary/50 hover:bg-accent/50 transition-colors max-w-xs">
                      {uploadingThumb ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-sm font-medium">Uploading...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3">
                          <div className="h-14 w-14 rounded-xl bg-muted flex items-center justify-center">
                            <ImageIcon className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">Upload cover image</p>
                            <p className="text-xs text-muted-foreground mt-1">JPG, PNG. Max 5MB.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                )}
              </CardContent>
            </Card>

            {/* Course Access Link — only for Course type */}
            {form.type === 'Course' && (
              <Card className="border-emerald-200 bg-emerald-50/30">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                      <Link2 className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Course Access Link</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">Link to your course videos (YouTube playlist, Google Drive, Teachable, etc.)</p>
                    </div>
                  </div>
                  
                  <Input
                    value={form.access_link}
                    onChange={e => updateForm('access_link', e.target.value)}
                    placeholder="https://youtube.com/playlist?list=... or Google Drive link"
                    className="h-11"
                    data-testid="product-form-access-link"
                  />

                  <div className="flex items-start gap-2 p-3 rounded-lg bg-white border border-emerald-100">
                    <Info className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="text-xs text-muted-foreground leading-relaxed space-y-1.5">
                      <p className="font-medium text-foreground">How course delivery works:</p>
                      <p>1. <strong>Upload file above</strong> — your course notes, PDF workbook, or supplementary materials</p>
                      <p>2. <strong>Paste access link here</strong> — link to your actual video course (YouTube unlisted playlist, Google Drive folder, etc.)</p>
                      <p>3. After purchase, customers receive <strong>both</strong>: a download link for the file + the access link to watch your course</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 4: Settings */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Product Settings</h2>
              <p className="text-sm text-muted-foreground mt-1">Configure download limits and access settings</p>
            </div>

            <Card>
              <CardContent className="pt-6 space-y-6">
                {/* Download Limits */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Max Downloads Per Customer</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">How many times each customer can download this product</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {[3, 5, 10, 20].map(n => (
                      <button
                        key={n}
                        onClick={() => updateForm('max_downloads', n)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors
                          ${form.max_downloads === n ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:border-primary/50'}`}
                      >
                        {n}x
                      </button>
                    ))}
                    <Input
                      type="number"
                      min="1"
                      value={form.max_downloads}
                      onChange={e => updateForm('max_downloads', parseInt(e.target.value) || 1)}
                      className="w-20 h-10"
                      data-testid="product-form-download-limit-input"
                    />
                  </div>
                </div>

                {/* Expiry */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Download Link Expiry</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">How long the download link remains active after purchase</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {[24, 48, 72, 168].map(h => (
                      <button
                        key={h}
                        onClick={() => updateForm('expiry_hours', h)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors
                          ${form.expiry_hours === h ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:border-primary/50'}`}
                      >
                        {h < 48 ? '24h' : h < 72 ? '2 days' : h < 168 ? '3 days' : '7 days'}
                      </button>
                    ))}
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min="1"
                        value={form.expiry_hours}
                        onChange={e => updateForm('expiry_hours', parseInt(e.target.value) || 1)}
                        className="w-20 h-10"
                        data-testid="product-form-expiry-input"
                      />
                      <span className="text-sm text-muted-foreground">hours</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Card */}
            <Card className="bg-accent/30">
              <CardContent className="pt-6">
                <h3 className="text-sm font-medium mb-3">Product Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="font-medium">{form.type}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium truncate max-w-[60%]">{form.name || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Price</span><span className="font-semibold">{form.is_free ? 'Free' : `${form.currency === 'IDR' ? 'Rp' : form.currency === 'USD' ? '$' : 'RM'} ${form.price ? parseFloat(form.price).toFixed(2) : '0.00'}`}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">File</span><span className="font-mono text-xs truncate max-w-[60%]">{form.file_name || 'Not uploaded'}</span></div>
                  {form.type === 'Course' && <div className="flex justify-between"><span className="text-muted-foreground">Access Link</span><span className="text-xs truncate max-w-[60%]">{form.access_link || 'Not set'}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Downloads</span><span>{form.max_downloads}x per customer</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Link Expiry</span><span>{form.expiry_hours} hours</span></div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t">
        <div>
          {currentStep > 0 && (
            <Button variant="outline" onClick={handleBack} size="lg">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {currentStep === STEPS.length - 1 ? (
            <>
              <Button variant="outline" size="lg" onClick={() => handlePublish(false)} disabled={saving}>
                Save as Draft
              </Button>
              <Button size="lg" onClick={() => handlePublish(true)} disabled={saving} data-testid="product-form-save-button" className="btn-press">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
                Publish Product
              </Button>
            </>
          ) : (
            <Button size="lg" onClick={handleNext} disabled={!canProceed()} className="btn-press">
              Continue <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
